import React from "react";
import { cn } from "@/lib/utils";

type ColorType = "primary" | "secondary" | "neutral" | string;
type ShadeType = "100" | "200" | "300" | "400" | "500" | "600";

interface RingerProps {
  size?: number;
  strokeWidth?: number;
  color?: ColorType;
  colorShade?: ShadeType;
  className?: string;
  speed?: number;
}

const Ringer: React.FC<RingerProps> = ({
  size = 40,
  strokeWidth = 4,
  color = "primary", // Default to primary color
  colorShade = "500", // Default to 500 shade
  className = "",
  speed = 1, // Animation speed multiplier
}) => {
  // SVG parameters
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  // Determine if we should use a Tailwind color class or a custom color
  const isTailwindColor =
    color === "primary" || color === "secondary" || color === "neutral";

  // Calculate the Tailwind color class name if applicable
  const colorClass = isTailwindColor ? `text-${color}-${colorShade}` : "";

  // Calculate animation duration based on speed
  const animationDuration = 2.4 / speed;

  return (
    <div
      className={cn("flex items-center justify-center", className)}
      style={{ width: size, height: size, margin: "auto" }}
    >
      <svg
        viewBox={`0 0 ${size} ${size}`}
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        className={colorClass}
        style={!isTailwindColor ? { color: color } : undefined}
      >
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          stroke="currentColor"
          strokeDasharray={`${circumference} ${circumference}`}
          strokeLinecap="round"
        >
          <animate
            attributeName="stroke-dashoffset"
            from={`${circumference * 2}`}
            to="0"
            dur={`${animationDuration}s`}
            repeatCount="indefinite"
            begin="0s"
          />
          <animateTransform
            attributeName="transform"
            type="rotate"
            from={`-90 ${center} ${center}`}
            to={`-90 ${center} ${center}`}
            dur={`${animationDuration}s`}
            repeatCount="indefinite"
            begin="0s"
          />
        </circle>
      </svg>
    </div>
  );
};

export default Ringer;
